import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { TOAST_TYPE } from '@base'
import BasicModal from '@base/basic/BasicModal'
import { Button, Input, Select } from '@baseV2'
import { CountryCodes, INSPECTION_USER_DESIGNATIONS, strings } from '@constants'
import { getUsersOfOrganisation, updateEnquiryCostMetrics, updateOrderCostMetrics } from '@data'
import { showLoader, showToast } from '@data/state/action'
import { getCountryDialCode, getNumberWithCountryCode, mobileLength } from '@utils'

import { CountryCodeWrapper, FieldErrorText, MobileFieldGroup, MobileRow, ModalActions, ModalBody, ModalTitle } from './styles'

const initialFormState = {
	full_name: {
		id: 'full_name',
		label: 'Name',
		placeholder: 'Enter full name',
		value: '',
		dirty: false,
		required: true,
		valid: null,
	},
	country_code: {
		key: 'country_code',
		value: CountryCodes[0],
		valid: true,
	},
	mobile_number: {
		id: 'mobile_number',
		label: 'Mobile',
		placeholder: 'Enter mobile number',
		value: '',
		dirty: false,
		required: true,
		valid: null,
	},
	email: {
		id: 'email',
		label: 'Email',
		placeholder: 'Enter email',
		value: '',
		dirty: false,
		required: true,
	},
	designation: {
		id: 'designation',
		label: 'Designation',
		placeholder: 'Enter designation',
		value: '',
		dirty: false,
		required: false,
	},
}

const deriveFormStateFromItemData = (oldState = initialFormState, data) => {
	const derivedState = { ...oldState }
	const dialCode = getCountryDialCode(data?.country_code || defaultCountryCode)

	derivedState.full_name = {
		...derivedState.full_name,
		value: data?.full_name || '',
		dirty: false,
	}
	derivedState.country_code = {
		...derivedState.country_code,
		value: CountryCodes.find((item) => item.code === data?.country_code) || defaultCountryCode,
		valid: true,
	}
	derivedState.mobile_number = {
		...derivedState.mobile_number,
		value: data?.mobile_number?.replace(dialCode, '')?.trim() || '',
		dirty: false,
		valid: null,
	}
	derivedState.email = {
		...derivedState.email,
		value: data?.email_id || data?.email || '',
		dirty: false,
	}
	derivedState.designation = {
		...derivedState.designation,
		value: data?.designation || '',
		dirty: false,
	}

	return derivedState
}

const defaultCountryCode = CountryCodes.find((_c) => _c.default)
const designationTypes = Object.entries(INSPECTION_USER_DESIGNATIONS).map(([key, value]) => ({
	key,
	label: value,
}))

const isValidEmail = (email) => {
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

const PurchaseManagerDetailsModal = ({ isOpen, onClose, enquiry, update, onSaved, order, editable, organisationId = null, viewOnly = false }) => {
	const dispatch = useDispatch()

	const [countryCode, setCountryCode] = useState(defaultCountryCode)
	const [formState, setFormState] = useState(initialFormState)
	const [userList, setUserList] = useState([])
	const [selectedUser, setSelectedUser] = useState(null)
	const user = useSelector((_state) => _state.getIn(['user', 'user']))

	useEffect(() => {
		if (!isOpen) return
		const initialData = order?.cost_metrics?.buyer_procurement_manager || enquiry?.cost_metrics?.buyer_procurement_manager
		if (initialData) {
			const derivedState = deriveFormStateFromItemData(initialFormState, initialData)
			setFormState(derivedState)
			setCountryCode(derivedState.country_code.value)
		}
	}, [order, enquiry, isOpen])

	useEffect(() => {
		if (!isOpen || !organisationId) return

		dispatch(showLoader(true))
		getUsersOfOrganisation(organisationId)
			.then((response) => {
				setUserList(response || [])
			})
			.catch((error) => {
				const error_msg = error?.response?.data?.message ?? strings('msg_failed_to_fetch')
				dispatch(showToast(true, error_msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}, [organisationId, isOpen])

	const updateState = (key, updates) => {
		setFormState((_oldState) => ({
			..._oldState,
			[key]: {
				..._oldState[key],
				...updates,
			},
		}))
	}

	const handleUserSelect = (value) => {
		setFormState((_oldState) => deriveFormStateFromItemData(_oldState, value))
		setSelectedUser(value)
	}

	const handleDesignationChange = (value) => {
		updateState('designation', { value: value?.key ?? '' })
	}

	const isUserSelectDisabled = useMemo(() => {
		const { full_name, mobile_number, email, designation } = formState
		return !selectedUser && Boolean(full_name.value || mobile_number.value || email.value || designation.value)
	}, [formState.full_name.value, formState.mobile_number.value, formState.email.value, formState.designation.value])

	const areAllFieldsDisabled = !!selectedUser || viewOnly

	const submitHandler = () => {
		const { full_name, country_code, mobile_number, email, designation } = formState

		if (!formState.email.value || !isValidEmail(formState.email.value)) {
			dispatch(showToast(true, strings('enter_valid_email'), { type: TOAST_TYPE.ERROR }))
			return
		}

		const buyerProcurementManager = {
			full_name: full_name.value,
			mobile_number: getNumberWithCountryCode(country_code?.value, mobile_number.value),
			email: email.value,
			designation: designation.value,
			country_code: country_code.value.code,
		}

		const updatedCostMetrics = order
			? { ...order.cost_metrics, buyer_procurement_manager: buyerProcurementManager }
			: { ...enquiry.cost_metrics, buyer_procurement_manager: buyerProcurementManager }

		dispatch(showLoader(true))
		const API = order
			? updateOrderCostMetrics(order?.order_id, user?.user_id, updatedCostMetrics)
			: updateEnquiryCostMetrics(enquiry?.enquiry_id, user?.user_id, updatedCostMetrics)

		API?.then(() => {
			dispatch(showToast(true, strings(['purchase', 'manager', 'details', 'updated', 'successfully']), { type: TOAST_TYPE.SUCCESS }))
			onSaved({ cost_metrics: updatedCostMetrics })
			update()
			onClose()
		})
			.catch((error) => {
				dispatch(showToast(true, error?.response?.data?.message, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}

	const resetHandler = () => {
		setFormState(initialFormState)
		setSelectedUser(null)
		setCountryCode(defaultCountryCode)
	}

	const formErrors = useMemo(() => {
		const errors = {}
		if (!formState.full_name.value?.trim()) {
			errors.full_name = strings('name_required')
		}
		if (!formState.mobile_number.value) {
			errors.mobile_number = strings('mob_num_required')
		} else if (String(formState.mobile_number.value).length !== mobileLength(countryCode.dial_code)) {
			errors.mobile_number = strings('enter_valid_num')
		}
		if (!formState.email.value || !isValidEmail(formState.email.value)) {
			errors.email = strings('enter_valid_email')
		}
		return errors
	}, [formState.full_name.value, formState.mobile_number.value, formState.email.value, countryCode.dial_code])

	const disabledHandler = useMemo(() => {
		if (viewOnly) return true
		if (selectedUser) return false
		return Object.keys(formErrors).length > 0
	}, [formErrors, selectedUser, viewOnly])

	const title = editable ? strings(['update', 'purchase', 'manager']) : strings(['add', 'purchase', 'manager'])

	return (
		<BasicModal isOpen={isOpen} onClose={onClose} width='40vw' closeIcon={true}>
			<ModalTitle>{title}</ModalTitle>
			<ModalBody>
				<Select
					id='select-user'
					label={strings('user')}
					placeholder={strings('select', 'user')}
					data={userList ?? []}
					value={userList?.find((item) => item.user_id === selectedUser?.user_id) ?? {}}
					primaryKey='user_id'
					displayKey='full_name'
					onChange={handleUserSelect}
					disabled={isUserSelectDisabled || viewOnly}
				/>

				<Input
					id={formState.full_name.id}
					label={formState.full_name.label}
					placeholder={formState.full_name.placeholder}
					value={formState.full_name.value ?? ''}
					required={formState.full_name.required}
					error={formState.full_name.dirty ? formErrors.full_name : undefined}
					onChange={(e) => updateState('full_name', { value: e.target.value, dirty: true })}
					disabled={areAllFieldsDisabled}
				/>

				<MobileFieldGroup>
					<MobileRow>
						<CountryCodeWrapper>
							<Select
								id='login-country-code'
								data={CountryCodes}
								displayKey='dial_code'
								primaryKey='name'
								type='tel'
								noDropIcon
								value={CountryCodes.find((item) => formState.country_code.value?.code === item.code) ?? CountryCodes[0]}
								onChange={(value) => {
									updateState('country_code', { value, dirty: true })
									setCountryCode(value)
								}}
								disabled={areAllFieldsDisabled}
							/>
						</CountryCodeWrapper>
						<Input
							id={formState.mobile_number.id}
							type='tel'
							label={formState.mobile_number.label}
							placeholder={formState.mobile_number.placeholder}
							value={formState.mobile_number.value ?? ''}
							maxLength={mobileLength(countryCode.dial_code)}
							required={formState.mobile_number.required}
							onChange={(e) => updateState('mobile_number', { value: e.target.value, dirty: true })}
							disabled={areAllFieldsDisabled}
						/>
					</MobileRow>
					{formState.mobile_number.dirty && formErrors.mobile_number && <FieldErrorText>{formErrors.mobile_number}</FieldErrorText>}
				</MobileFieldGroup>

				<Input
					id={formState.email.id}
					label={formState.email.label}
					type='email'
					placeholder={formState.email.placeholder}
					value={formState.email.value}
					required={formState.email.required}
					error={formState.email.dirty ? formErrors.email : undefined}
					onChange={(e) => updateState('email', { value: e.target.value, dirty: true })}
					disabled={areAllFieldsDisabled || selectedUser?.email_id}
				/>

				<Select
					id='select-designation'
					label={strings('designation')}
					placeholder={strings('select', 'designation')}
					data={designationTypes ?? []}
					value={designationTypes.find((item) => item.key === formState.designation.value) ?? {}}
					disabled={areAllFieldsDisabled}
					primaryKey='key'
					displayKey='label'
					required={formState.designation.required}
					onChange={handleDesignationChange}
				/>
			</ModalBody>

			<ModalActions>
				<Button variant='ghost' color='danger' size='small' onClick={resetHandler} disabled={viewOnly}>
					{strings('reset')}
				</Button>
				<Button size='small' disabled={disabledHandler} onClick={submitHandler}>
					{editable ? strings('update') : strings('add')}
				</Button>
			</ModalActions>
		</BasicModal>
	)
}

export default PurchaseManagerDetailsModal
